import { useState, useEffect } from 'react';

// Utility to fetch current ETH/USD price and calculate mint price in USD
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
let cachedPrice = null;
let cacheTimestamp = null;

/**
 * Fetches current ETH/USD price from CoinGecko API
 * @returns {Promise<number>} Current ETH price in USD
 */
export const getEthPrice = async () => {
  // Return cached price if still fresh
  if (cachedPrice && cacheTimestamp && Date.now() - cacheTimestamp < CACHE_DURATION) {
    return cachedPrice;
  }

  try {
    const response = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd'
    );

    if (!response.ok) {
      throw new Error('Failed to fetch ETH price');
    }

    const data = await response.json();
    const price = data.ethereum?.usd;

    if (!price) {
      throw new Error('ETH price not found in response');
    }

    // Cache the price
    cachedPrice = price;
    cacheTimestamp = Date.now();

    return price;
  } catch (error) {
    console.error('Error fetching ETH price:', error);

    // Return fallback price if API fails (~$3000 as of early 2024)
    // This ensures the app doesn't break if CoinGecko is down
    return 3000;
  }
};

/**
 * Calculates USD price for minting based on current ETH price
 * Adds a small buffer for gas fees (~15%)
 * @param {number} ethAmount Amount of ETH (e.g., 0.0001)
 * @returns {Promise<string>} USD price rounded to 2 decimals
 */
export const calculateMintPriceUSD = async (ethAmount) => {
  const ethPrice = await getEthPrice();
  const basePrice = ethAmount * ethPrice;

  // Add 15% buffer for gas fees
  const priceWithBuffer = basePrice * 1.15;

  // Round to 2 decimal places
  return priceWithBuffer.toFixed(2);
};

/**
 * Hook to get live USD price for display
 * @param {number} ethAmount Amount of ETH
 * @returns {string} Formatted USD price
 */
export const useMintPriceUSD = (ethAmount) => {
  const [usdPrice, setUsdPrice] = useState('...');

  useEffect(() => {
    calculateMintPriceUSD(ethAmount).then(price => {
      setUsdPrice(price);
    });
  }, [ethAmount]);

  return usdPrice;
};
